// alpine component for the chat message input (resources/views/components/chats/message-input.blade.php).
// registered here in the bundle — not in a Livewire @script block — so the component
// definition exists before Alpine starts and survives morphs and wire:navigate
// (an @script registration can be missing when Alpine re-initializes the tree,
// making x-data="MessageInput" resolve to undefined and child expressions like
// isDraggingFile throw). server-side values (limits, translations) come in via
// the config argument: x-data="MessageInput(@js($getAlpineConfig()))".

function registerMessageInput() {
    window.Alpine.data('MessageInput', (config = {}) => {
        return {
            isRecording: false,
            isLoading: false,
            isUploading: false,

            isDraggingFile: false,
            filePreviews: [],
            uploadScope: null,
            activeUpload: null,
            pendingFileRemovals: 0,
            destroyed: false,

            recordingSeconds: 0,
            recordingTimer: null,
            audioLevel: 0,
            audioContext: null,
            levelMeterFrame: null,
            voicePreviewUrl: null,
            maxLength: config.maxLength ?? 1000,
            canAttachFiles: config.canAttachFiles ?? false,
            limits: config.limits ?? {},
            messages: config.messages ?? {},
            onTemplateSelectedForDraft: null,
            onTemplateSelectedForSend: null,

            get characterCount() {
                return this.$wire.messageInput ? this.$wire.messageInput.length : 0;
            },

            get recordingTimeDisplay() {
                const minutes = Math.floor(this.recordingSeconds / 60);
                const seconds = this.recordingSeconds % 60;
                return `${minutes}:${String(seconds).padStart(2, '0')}`;
            },

            get hasFileAttachments() {
                return this.filePreviews.length > 0 || Object.keys(this.$wire.fileAttachments ?? {}).length > 0;
            },

            get isFileUploading() {
                return this.filePreviews.some(file => ['queued', 'uploading'].includes(file.status));
            },

            get canSubmit() {
                return (
                    !this.isLoading &&
                    !this.isUploading &&
                    !this.isRecording &&
                    this.pendingFileRemovals === 0 &&
                    this.filePreviews.every(file => file.status === 'ready')
                );
            },

            init() {
                this.uploadScope = this.$wire.attachmentUploadToken;
                this.$watch('$wire.attachmentUploadToken', scope => {
                    if (scope !== this.uploadScope) {
                        this.clearFilePreview();
                        this.uploadScope = scope;
                    }
                });
                // Watch for voice attachment changes to clear uploading state
                this.$watch('$wire.voiceAttachment', value => {
                    if (value) {
                        this.isUploading = false;
                    }
                });

                // Listen for template draft selection
                this.onTemplateSelectedForDraft = event => {
                    if (event.detail && event.detail.content) {
                        // Set the message input content
                        this.$wire.set('messageInput', event.detail.content);

                        // Focus and resize the textarea
                        this.$nextTick(() => {
                            this.$refs.textarea?.focus();

                            setTimeout(() => {
                                this.resizeTextarea();
                            }, 300);
                        });
                    }
                };
                window.addEventListener('template-selected-for-draft', this.onTemplateSelectedForDraft);

                // Listen for template send selection
                this.onTemplateSelectedForSend = () => {
                    this.$nextTick(() => {
                        this.$wire.call('loadLatestMessages');
                    });
                };
                window.addEventListener('template-selected-for-send', this.onTemplateSelectedForSend);
            },

            get submitButtonState() {
                const defaultState =
                    !this.hasFileAttachments &&
                    !this.$wire.voiceAttachment &&
                    !this.isRecording &&
                    !this.isLoading &&
                    !this.isUploading &&
                    !this.isFileUploading;

                if (defaultState) {
                    return 'default';
                }

                const loadingState = this.isLoading;

                if (loadingState) {
                    return 'loading';
                }

                const recordingState = this.isRecording;

                if (recordingState) {
                    return 'recording';
                }

                const uploadingState = this.isUploading && !this.$wire.voiceAttachment;

                if (uploadingState) {
                    return 'uploading';
                }

                // a staged voice note wins over caption text so the
                // trash/preview affordances stay visible while typing
                const withVoiceState = this.$wire.voiceAttachment;

                if (withVoiceState) {
                    return 'with-voice';
                }

                const withTextOrFilesState = this.hasFileAttachments || this.$wire.messageInput;

                if (withTextOrFilesState) {
                    return 'with-text-or-files';
                }

                return 'unknown';
            },

            toggleRecording() {
                if (this.submitButtonState === 'recording') {
                    this.stopRecording();
                } else if (['default', 'with-voice'].includes(this.submitButtonState)) {
                    this.startRecording();
                }
            },

            async destroy() {
                this.destroyed = true;
                window.removeEventListener('template-selected-for-draft', this.onTemplateSelectedForDraft);
                window.removeEventListener('template-selected-for-send', this.onTemplateSelectedForSend);

                this.stopRecordingTimer();
                this.stopLevelMeter();
                this.clearVoicePreview();
                this.clearFilePreview();

                if (this.isRecording && window.VoiceRecorder) {
                    await window.VoiceRecorder.cancelRecording();
                }
            },

            openMessageTemplateModal() {
                this.$dispatch('open-modal', {
                    id: 'message-template-modal',
                    contactChatIds: [this.$wire.selectedChat?.id],
                });
            },

            openNotesModal() {
                this.$dispatch('open-modal', {
                    id: 'notes-modal',
                    contact_chat_id: this.$wire.selectedChat?.id,
                });
            },

            openOrdersModal() {
                this.$dispatch('open-modal', {
                    id: 'conversation-orders-modal',
                    contact_chat_id: this.$wire.selectedChat?.id,
                });
            },

            openFeedbackModal() {
                this.$dispatch('open-modal', {
                    id: 'feedback-modal',
                    contact_chat_id: this.$wire.selectedChat?.id,
                });
            },

            async startRecording() {
                if (this.isRecording || this.hasFileAttachments || !window.VoiceRecorder) {
                    return;
                }

                try {
                    // Check if device can record
                    const canRecord = await window.VoiceRecorder.canRecord();
                    if (!canRecord) {
                        new FilamentNotification()
                            .title(this.messages.deviceUnsupportedTitle)
                            .body(this.messages.deviceUnsupportedBody)
                            .color('danger')
                            .send();
                        return;
                    }

                    // Check and request permissions
                    const hasPermission = await window.VoiceRecorder.hasPermission();
                    if (!hasPermission) {
                        const permissionGranted = await window.VoiceRecorder.requestPermission();
                        if (!permissionGranted) {
                            new FilamentNotification()
                                .title(this.messages.micPermissionTitle)
                                .body(this.messages.micPermissionBody)
                                .color('danger')
                                .send();
                            return;
                        }
                    }

                    // Start recording
                    await window.VoiceRecorder.startRecording();
                    this.isRecording = true;
                    this.clearVoicePreview();
                    this.startRecordingTimer();
                    this.startLevelMeter();
                } catch (error) {
                    console.error('Voice recording error:', error);
                    this.isRecording = false;
                    this.stopRecordingTimer();
                    this.stopLevelMeter();
                    new FilamentNotification()
                        .title(this.messages.startRecordingErrorTitle)
                        .body(this.messages.startRecordingErrorBody)
                        .color('danger')
                        .send();
                }
            },

            async stopRecording() {
                if (!this.isRecording || !window.VoiceRecorder) {
                    return;
                }

                this.stopRecordingTimer();
                this.stopLevelMeter();

                try {
                    // Stop recording and get the file
                    const file = await window.VoiceRecorder.stopRecording();
                    this.isRecording = false;
                    this.isUploading = true; // Start uploading state

                    if (!file) {
                        this.isUploading = false;
                        new FilamentNotification()
                            .title(this.messages.saveRecordingErrorTitle)
                            .body(this.messages.saveRecordingErrorBody)
                            .color('danger')
                            .send();
                        return;
                    }

                    // Check file size
                    if (file.size > this.limits.audio) {
                        this.isUploading = false;
                        new FilamentNotification()
                            .title(this.messages.voiceTooLargeTitle)
                            .body(this.messages.voiceTooLargeBody)
                            .color('danger')
                            .send();

                        this.cancelVoiceNote();
                        return;
                    }

                    // Local playback preview of the recorded note
                    this.voicePreviewUrl = URL.createObjectURL(file);

                    // Upload the file
                    const dt = new DataTransfer();
                    dt.items.add(file);
                    this.$refs.voiceInput.files = dt.files;
                    this.$refs.voiceInput.dispatchEvent(
                        new Event('change', {
                            bubbles: true,
                        })
                    );
                } catch (error) {
                    console.error('Stop recording error:', error);
                    this.isRecording = false;
                    this.isUploading = false;
                    new FilamentNotification()
                        .title(this.messages.stopRecordingErrorTitle)
                        .body(this.messages.stopRecordingErrorBody)
                        .color('danger')
                        .send();
                }
            },

            async cancelVoiceNote() {
                // If recording is active, stop it first
                if (this.isRecording && window.VoiceRecorder) {
                    try {
                        await window.VoiceRecorder.cancelRecording();
                    } catch (error) {
                        console.error('Error canceling recording:', error);
                    }
                }

                this.stopRecordingTimer();
                this.stopLevelMeter();
                this.clearVoicePreview();
                this.$wire.voiceAttachment = null;
                this.isRecording = false;
                this.isUploading = false;
                this.$refs.voiceInput.value = null;
                this.$refs.voiceInput.dispatchEvent(
                    new Event('change', {
                        bubbles: true,
                    })
                );
            },

            startRecordingTimer() {
                this.recordingSeconds = 0;
                this.recordingTimer = setInterval(() => {
                    this.recordingSeconds++;

                    // auto-stop before recordings grow past the upload limit
                    if (this.recordingSeconds >= 600) {
                        this.stopRecording();
                    }
                }, 1000);
            },

            stopRecordingTimer() {
                if (this.recordingTimer) {
                    clearInterval(this.recordingTimer);
                    this.recordingTimer = null;
                }
            },

            startLevelMeter() {
                const stream = window.VoiceRecorder?.getActiveStream?.();

                if (!stream || !window.AudioContext) {
                    return; // native recorder: no stream to observe
                }

                try {
                    this.audioContext = new AudioContext();
                    const source = this.audioContext.createMediaStreamSource(stream);
                    const analyser = this.audioContext.createAnalyser();
                    analyser.fftSize = 256;
                    source.connect(analyser);
                    const data = new Uint8Array(analyser.frequencyBinCount);

                    const tick = () => {
                        if (!this.isRecording) {
                            return;
                        }

                        analyser.getByteTimeDomainData(data);
                        let sum = 0;
                        for (let i = 0; i < data.length; i++) {
                            const value = (data[i] - 128) / 128;
                            sum += value * value;
                        }
                        this.audioLevel = Math.min(100, Math.round(Math.sqrt(sum / data.length) * 300));
                        this.levelMeterFrame = requestAnimationFrame(tick);
                    };

                    this.levelMeterFrame = requestAnimationFrame(tick);
                } catch (error) {
                    // the level meter is best-effort; recording works without it
                }
            },

            stopLevelMeter() {
                if (this.levelMeterFrame) {
                    cancelAnimationFrame(this.levelMeterFrame);
                    this.levelMeterFrame = null;
                }

                if (this.audioContext) {
                    this.audioContext.close().catch(() => {});
                    this.audioContext = null;
                }

                this.audioLevel = 0;
            },

            clearVoicePreview() {
                if (this.voicePreviewUrl) {
                    URL.revokeObjectURL(this.voicePreviewUrl);
                    this.voicePreviewUrl = null;
                }
            },

            resizeTextarea() {
                if (!this.$refs.textarea) {
                    return;
                }

                this.$refs.textarea.style.height = '44px';
                const newHeight = Math.min(this.$refs.textarea.scrollHeight, 44 * 4);
                this.$refs.textarea.style.height = newHeight + 'px';
            },

            validateFileSize(file) {
                if (!file) {
                    return;
                }

                const size = file.size;
                const mimeType = file.type;
                let limit = 0;
                let errorMessage = '';

                if (mimeType.startsWith('image/')) {
                    // platform-aware limit (e.g. telegram allows larger images than whatsapp)
                    limit = this.limits.image;
                    errorMessage = this.messages.imageTooLarge;
                } else if (mimeType.startsWith('video/')) {
                    limit = this.limits.video;
                    errorMessage = this.messages.videoTooLarge;
                } else if (mimeType.startsWith('audio/')) {
                    limit = this.limits.audio;
                    errorMessage = this.messages.audioTooLarge;
                } else if (
                    [
                        'application/pdf',
                        'application/msword',
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                        'text/plain',
                    ].includes(mimeType)
                ) {
                    limit = this.limits.document;
                    errorMessage = this.messages.documentTooLarge;
                } else {
                    limit = this.limits.document;
                    errorMessage = this.messages.fileTooLarge;
                }

                if (size > limit) {
                    new FilamentNotification().title(errorMessage).color('danger').send();

                    return false;
                }

                return true;
            },

            clearFilePreview() {
                if (this.activeUpload) {
                    this.$wire.cancelUpload('fileAttachments.' + this.activeUpload);
                }
                for (const file of this.filePreviews) {
                    if (file.thumbnail) URL.revokeObjectURL(file.thumbnail);
                }
                this.filePreviews = [];
            },

            async removeFileAttachment(id) {
                const file = this.filePreviews.find(file => file.id === id);
                if (!file || this.isLoading) return;
                if (this.activeUpload === id) {
                    this.$wire.cancelUpload('fileAttachments.' + id);
                }
                if (file.thumbnail) URL.revokeObjectURL(file.thumbnail);
                this.filePreviews = this.filePreviews.filter(file => file.id !== id);
                await this.removeStagedFile(id);
            },

            async removeStagedFile(id) {
                this.pendingFileRemovals++;
                try {
                    await this.$wire.removeFileAttachment(id);
                } finally {
                    this.pendingFileRemovals--;
                }
            },

            retryFileAttachment(id) {
                const file = this.filePreviews.find(file => file.id === id);
                if (file && !this.isLoading) {
                    file.status = 'queued';
                    file.progress = 0;
                    this.uploadNextFile();
                }
            },

            syncFilePreviews() {
                const remaining = this.$wire.fileAttachments ?? {};
                this.filePreviews = this.filePreviews.filter(file => {
                    if (file.status !== 'ready' || Object.hasOwn(remaining, file.id)) return true;
                    if (file.thumbnail) URL.revokeObjectURL(file.thumbnail);
                    return false;
                });
            },

            async uploadNextFile() {
                if (this.activeUpload || this.destroyed) return;
                const file = this.filePreviews.find(file => file.status === 'queued');
                if (!file) return;
                this.activeUpload = file.id;
                file.status = 'uploading';
                const scope = this.uploadScope;
                await new Promise(resolve => {
                    this.$wire.upload(
                        'fileAttachments.' + file.id,
                        file.file,
                        () => {
                            if (!this.destroyed && scope === this.uploadScope && this.filePreviews.includes(file)) {
                                file.status = 'ready';
                                file.progress = 100;
                            } else if (!this.destroyed && scope === this.uploadScope) {
                                // cancellation can race with the server accepting the upload
                                this.removeStagedFile(file.id);
                            }
                            resolve();
                        },
                        () => {
                            file.status = 'error';
                            resolve();
                        },
                        event => {
                            file.progress = event.detail.progress;
                        },
                        () => resolve()
                    );
                });
                this.activeUpload = null;
                this.uploadNextFile();
            },

            readableSize(bytes) {
                const units = ['B', 'KB', 'MB', 'GB'];
                let value = bytes;
                let unit = 0;

                while (value >= 1024 && unit < units.length - 1) {
                    value /= 1024;
                    unit++;
                }

                return `${value.toFixed(value >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`;
            },

            attachFiles(files) {
                if (
                    !this.canAttachFiles ||
                    this.$wire.voiceAttachment ||
                    this.isRecording ||
                    this.isUploading ||
                    this.isLoading
                )
                    return;
                for (const file of Array.from(files ?? [])) {
                    if (!this.validateFileSize(file)) continue;
                    this.filePreviews.push({
                        id: this.uploadScope + '_' + crypto.randomUUID(),
                        file,
                        name: file.name,
                        size: this.readableSize(file.size),
                        thumbnail: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
                        progress: 0,
                        status: 'queued',
                    });
                }
                this.uploadNextFile();
            },

            handlePaste(event) {
                const files = event.clipboardData?.files;

                if (!files || files.length === 0 || !this.canAttachFiles) {
                    return;
                }

                event.preventDefault();
                this.attachFiles(files);
            },

            handleDragOver() {
                if (!this.canAttachFiles || this.$wire.voiceAttachment || this.isLoading) {
                    return;
                }

                this.isDraggingFile = true;
            },

            handleDragLeave(event) {
                // ignore dragleave fired when moving between child elements
                if (event.currentTarget.contains(event.relatedTarget)) {
                    return;
                }

                this.isDraggingFile = false;
            },

            handleDrop(event) {
                this.isDraggingFile = false;
                const files = event.dataTransfer?.files;

                if (files?.length) {
                    this.attachFiles(files);
                }
            },

            sendMessage($event) {
                if ($event.shiftKey) {
                    return;
                }

                $event.preventDefault();
                if (!this.canSubmit) return;
                this.isLoading = true;

                const startTime = Date.now();

                this.$wire
                    .sendMessage()
                    .then(() => {
                        this.syncFilePreviews();
                        if (!this.$wire.voiceAttachment) this.clearVoicePreview();
                        this.resizeTextarea();
                    })
                    .catch(error => {
                        if (window.Sentry) {
                            window.Sentry.captureException(error, {
                                tags: {
                                    component: 'message-input',
                                    action: 'sendMessage',
                                },
                                extra: {
                                    duration: Date.now() - startTime,
                                    hasFileAttachment: !!this.hasFileAttachments,
                                    hasVoiceAttachment: !!this.$wire.voiceAttachment,
                                    messageLength: this.$wire.messageInput?.length || 0,
                                },
                            });
                        }

                        new FilamentNotification().title(this.messages.sendFailed).color('danger').send();
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            },
        };
    });
}

// module scripts run before Livewire starts Alpine, so alpine:init is the normal
// path; the direct call covers the edge where Alpine is already up when this loads
document.addEventListener('alpine:init', registerMessageInput);

if (window.Alpine) {
    registerMessageInput();
}
